// The legality core: what a roll of four dice lets you do.
//
// Everything here is a pure function of plain data. The rules being encoded
// (docs/rules.pdf, p1-2, "Rolling"):
//
//  - The four dice pair up "any way you wish" - three distinct pairings.
//  - Each pair's sum advances a white runner in that column: an already-
//    placed runner moves up one; otherwise a free runner is placed one step
//    ahead of your own committed marker, or on the bottom space.
//  - "You must always play to as many columns as your roll allows" - WITHIN
//    the pairing you chose, both sums must be played if both can be. The
//    choice OF pairing is free (that is the game).
//  - A claimed column "has effectively ceased to exist" (p2) - no runner may
//    enter it, for anyone, ever.
//  - A runner already on a column's top space cannot advance further, so its
//    sum stops being playable.
//  - If no pairing allows any move at all, the roll is a bust.

import { COLUMN_HEIGHTS, RUNNER_COUNT } from './constants';

/**
 * The three ways four dice split into two pairs, as sum-pairs, deduplicated.
 * Doubles collapse pairings that produce identical sums - "if you roll
 * doubles / triples / quadruples, you'll have fewer combinations" (p1).
 */
export const pairingsOf = (dice) => {
  const [a, b, c, d] = dice;
  const raw = [
    [a + b, c + d],
    [a + c, b + d],
    [a + d, b + c],
  ];
  const seen = new Set();
  const result = [];
  for (const pair of raw) {
    const key = [...pair].sort((x, y) => x - y).join('+');
    if (!seen.has(key)) {
      seen.add(key);
      result.push([...pair].sort((x, y) => x - y));
    }
  }
  return result;
};

/**
 * The context a legality question needs, pulled out of full game state so
 * the bot can ask hypotheticals cheaply.
 *
 *   claimed  - { [column]: playerId } for columns already won
 *   markers  - the active player's committed markers { [column]: step }
 *   runners  - this turn's white-runner positions { [column]: step }
 */
export const moveContext = (state) => ({
  claimed: state.columns,
  markers: state.players[state.meta.activePlayerIndex].markers || {},
  runners: state.turn.runners || {},
});

/** Where a newly placed or advanced runner in `column` would land. */
export const nextStepIn = (column, { markers, runners }) => {
  if (runners[column] !== undefined) return runners[column] + 1;
  return (markers[column] || 0) + 1;
};

/** Can the active player advance in `column` at all, right now? */
export const canAdvanceIn = (column, ctx) => {
  if (ctx.claimed[column]) return false;
  const onColumn = ctx.runners[column] !== undefined;
  if (!onColumn && Object.keys(ctx.runners).length >= RUNNER_COUNT) return false;
  return nextStepIn(column, ctx) <= COLUMN_HEIGHTS[column];
};

/**
 * What one chosen pairing lets you play: `[s1, s2]` if both sums can be
 * used together, otherwise each singly-usable sum as its own alternative.
 *
 * Returns a list of "plays", each a sorted array of the sums advanced.
 * "If both totals can be used, both must be used" - so when the pair is
 * jointly playable, the single-sum fallbacks are NOT offered.
 *
 * The one non-obvious joint case: two sums that are both NEW columns when
 * only one free runner remains. Each is playable alone; together they need
 * two runners. The player then chooses which column gets the last runner.
 */
export const playsForPairing = (pairing, ctx) => {
  const [s1, s2] = pairing;

  if (s1 === s2) {
    // Same column twice: advance twice if the column has the headroom,
    // "you can play twice in a single column" (p2) - else once, else nothing.
    if (!canAdvanceIn(s1, ctx)) return [];
    const landing = nextStepIn(s1, ctx);
    return landing + 1 <= COLUMN_HEIGHTS[s1] ? [[s1, s1]] : [[s1]];
  }

  const first = canAdvanceIn(s1, ctx);
  const second = canAdvanceIn(s2, ctx);
  if (!first && !second) return [];
  if (first && second) {
    // Jointly playable unless they'd both need a new runner and only one
    // runner is free.
    const needRunner = (s) => ctx.runners[s] === undefined;
    const freeRunners = RUNNER_COUNT - Object.keys(ctx.runners).length;
    if (needRunner(s1) && needRunner(s2) && freeRunners < 2) {
      return [[s1], [s2]];
    }
    return [[s1, s2]];
  }
  return [first ? [s1] : [s2]];
};

/**
 * Every distinct play this roll offers, across all pairings. Empty = bust.
 *
 * Each option carries the pairing it came from so the UI can show "3+4 & 2+6"
 * next to "7 & 8". Deduplicated by the sums actually advanced - two pairings
 * that move the same runners the same way are one choice, not two.
 */
export const optionsForRoll = (dice, ctx) => {
  const seen = new Set();
  const options = [];
  for (const pairing of pairingsOf(dice)) {
    for (const sums of playsForPairing(pairing, ctx)) {
      const key = sums.join('+');
      if (!seen.has(key)) {
        seen.add(key);
        options.push({ sums, pairing });
      }
    }
  }
  return options;
};

/**
 * Exact probability that the NEXT roll busts, given the current runners.
 *
 * A roll is survivable iff some pairing contains at least one sum you can
 * advance in - and whether a SUM is advanceable doesn't depend on the rest
 * of the roll (canAdvanceIn is per-column), so the whole question reduces to
 * an 11-entry playable table and a sweep of the 1296 outcomes against it.
 * The table is built from the same canAdvanceIn the dispatch path uses, so
 * the bot's caution and the engine's bust call can never drift apart.
 *
 * (One case worth convincing yourself of: a doubled sum [s, s] where the
 * runner sits one space below the top. The pair is still survivable - you
 * advance once and the second use is simply unavailable - and canAdvanceIn
 * agrees, because it asks about ONE advance.)
 */
export const bustProbability = (ctx) => {
  const playable = {};
  for (let sum = 2; sum <= 12; sum += 1) playable[sum] = canAdvanceIn(sum, ctx);

  let busts = 0;
  for (let a = 1; a <= 6; a += 1) {
    for (let b = 1; b <= 6; b += 1) {
      for (let c = 1; c <= 6; c += 1) {
        for (let d = 1; d <= 6; d += 1) {
          const survives = playable[a + b] || playable[c + d]
            || playable[a + c] || playable[b + d]
            || playable[a + d] || playable[b + c];
          if (!survives) busts += 1;
        }
      }
    }
  }
  return busts / 1296;
};
